import React, { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/apiClient";
import { useContent } from "@/context/ContentContext";
import { PageHeader } from "@/components/PageHeader";
import { Reveal } from "@/components/Reveal";
import { toast } from "sonner";
import { ArrowUpRight } from "lucide-react";

const field = "w-full bg-transparent border-b border-black/25 py-4 text-lg focus:border-black focus:outline-none transition-colors placeholder:text-zinc-400";

export default function Contact() {
  const { content } = useContent();
  const s = content?.settings || {};
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await api.post("/contact", form);
      toast.success(data.message || "Message sent!");
      setForm({ name: "", email: "", company: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to send");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <PageHeader eyebrow="Contact" title="Let's talk about your next project." />
      <section className="bg-[#FAFAFA] py-20 md:py-28 px-6 md:px-12 lg:px-16">
        <div className="max-w-[1400px] mx-auto grid grid-cols-1 md:grid-cols-12 gap-16">
          <Reveal className="md:col-span-5 space-y-10">
            <div>
              <p className="font-mono-t text-xs uppercase tracking-[0.2em] text-zinc-500 mb-3">Email</p>
              <a href={`mailto:${s.contact_email}`} className="text-2xl font-display tracking-tight link-underline">{s.contact_email}</a>
            </div>
            <div>
              <p className="font-mono-t text-xs uppercase tracking-[0.2em] text-zinc-500 mb-3">Phone</p>
              <a href={`tel:${s.contact_phone}`} className="text-2xl font-display tracking-tight link-underline">{s.contact_phone}</a>
            </div>
            <div>
              <p className="font-mono-t text-xs uppercase tracking-[0.2em] text-zinc-500 mb-3">Studio</p>
              <p className="text-2xl font-display tracking-tight">{s.contact_address}</p>
            </div>
          </Reveal>

          <Reveal className="md:col-span-6 md:col-start-7">
            <form onSubmit={submit} data-testid="contact-form" className="space-y-8">
              <input required data-testid="contact-name" className={field} placeholder="Your name" value={form.name} onChange={set("name")} />
              <input required type="email" data-testid="contact-email" className={field} placeholder="Email address" value={form.email} onChange={set("email")} />
              <input data-testid="contact-company" className={field} placeholder="Company (optional)" value={form.company} onChange={set("company")} />
              <textarea required rows={4} data-testid="contact-message" className={field} placeholder="Tell us about your project" value={form.message} onChange={set("message")} />
              <button type="submit" disabled={sending} data-testid="contact-submit" className="btn-fill inline-flex items-center gap-2 border border-black px-8 py-4 font-semibold disabled:opacity-50">
                {sending ? "Sending…" : "Send message"} <ArrowUpRight size={20} />
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
